import { useState } from 'react'
import {
  Avatar,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useRemoveProjectTeamMutation } from '../../../../../api/projects'
import type { IProjectParticipatingTeam } from '../../../../../api/types/projects'
import { formatNumber } from '../../../../../common/FormatNumber'
import { routesProfile } from '../../../../../consts'
import { extractApiErrorMessage } from '../../../../../utils/api'

type RemoveTarget = {
  teamId: number
  name: string
}

type Props = {
  projectId: number
  teams: IProjectParticipatingTeam[]
}

export function ProjectParticipatingTeamsCard({ projectId, teams }: Props) {
  const navigate = useNavigate()
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null)
  const [removeProjectTeam, removeState] = useRemoveProjectTeamMutation()

  const handleConfirmRemove = async () => {
    if (!removeTarget) {
      return
    }

    try {
      await removeProjectTeam({
        projectId,
        teamId: removeTarget.teamId,
      }).unwrap()
      setRemoveTarget(null)
      toast.success('Команда убрана из проекта')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось убрать команду из проекта')
    }
  }

  return (
    <>
      <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
        <Stack gap={2}>
          <Typography variant="black-16-semiBold">Команды в проекте</Typography>
          <Typography variant="black-14-light" color="text.secondary">
            Команды, которые участвуют в проекте. Участники, добавленные через команду, отображаются в составе
            проекта.
          </Typography>

          {teams.length === 0 ? (
            <Typography variant="black-14-light">Пока нет команд в проекте.</Typography>
          ) : (
            <Stack gap={1.5}>
              {teams.map((team) => {
                const teamPath = `/${routesProfile.TEAMS.path}/${team.slug || team.id}`
                const membersCount = Number(team.counters?.members ?? 0)

                return (
                  <Stack
                    key={team.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    gap={1}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" gap={1.25} alignItems="flex-start" minWidth={0} flex={1}>
                      <Avatar
                        src={team.image ?? undefined}
                        alt={team.name}
                        sx={{ width: 40, height: 40, cursor: 'pointer' }}
                        onClick={() => navigate(teamPath)}
                      >
                        {team.name.trim().charAt(0) || '?'}
                      </Avatar>
                      <Stack minWidth={0} flex={1}>
                        <Typography
                          variant="black-14-medium"
                          noWrap
                          sx={{ cursor: 'pointer', width: 'fit-content', maxWidth: '100%' }}
                          onClick={() => navigate(teamPath)}
                        >
                          {team.name}
                        </Typography>
                        {team.slogan?.trim() && (
                          <Typography variant="black-12-light" color="text.secondary" noWrap>
                            {team.slogan}
                          </Typography>
                        )}
                        <Typography variant="black-12-light" color="text.secondary">
                          Участников в команде: {formatNumber(membersCount)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Button
                      variant="danger"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation()
                        setRemoveTarget({ teamId: team.id, name: team.name })
                      }}
                      disabled={removeState.isLoading}
                      sx={{ flexShrink: 0 }}
                    >
                      Убрать из проекта
                    </Button>
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Stack>
      </Card>

      <Dialog open={removeTarget !== null} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Убрать команду из проекта</DialogTitle>
        <DialogContent>
          <Typography variant="black-14-normal">
            {removeTarget
              ? `Команда «${removeTarget.name}» будет исключена из проекта. Участники, добавленные через эту команду, также будут исключены из состава проекта. Продолжить?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="follow" onClick={() => setRemoveTarget(null)} disabled={removeState.isLoading}>
            Отмена
          </Button>
          <Button variant="danger" onClick={() => void handleConfirmRemove()} disabled={removeState.isLoading}>
            {removeState.isLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Убираем…</span>
              </Stack>
            ) : (
              'Убрать'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
